import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/auth';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface AppGuardProps {
  children: React.ReactNode;
}

export function AppGuard({ children }: AppGuardProps) {
  const { user, profile, loading, initialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't run navigation logic until auth is initialized
    if (!initialized || loading) return;

    console.log('AppGuard: Checking navigation', {
      hasUser: !!user,
      hasProfile: !!profile,
      pathname: location.pathname
    });

    if (!user) {
      console.log('AppGuard: Redirecting to login');
      navigate('/login', { 
        replace: true,
        state: { returnTo: location.pathname }
      });
      return;
    }

    // Only check onboarding if we have a profile
    if (profile) {
      const needsOnboarding = !profile.onboarding_progress?.isComplete;
      const isOnboardingPath = location.pathname === '/onboarding';

      console.log('AppGuard: Checking onboarding', {
        needsOnboarding,
        isOnboardingPath
      });

      if (needsOnboarding && !isOnboardingPath) {
        console.log('AppGuard: Redirecting to onboarding');
        navigate('/onboarding', { replace: true });
        return;
      }

      if (!needsOnboarding && isOnboardingPath) {
        console.log('AppGuard: Redirecting to dashboard');
        navigate('/dashboard', { replace: true });
        return;
      }
    }
  }, [user, profile, initialized, loading, navigate, location.pathname]);

  if (!initialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  return <>{children}</>;
}